import { Badge } from "shared/ui/badge"
import { Button } from "shared/ui/button"
import { Card, CardContent } from "shared/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Calendar, Loader2, MapPin, User as UserIcon } from "lucide-react"
import type { PermitListItem } from "modules/permit/api/permitService"

interface ReferencePermitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  search: string
  onSearchChange: (value: string) => void
  items: PermitListItem[]
  isLoading: boolean
  error: string | null
  isApplying: boolean
  onApply: (permitId: string) => void
}

export function ReferencePermitDialog({
  open,
  onOpenChange,
  search,
  onSearchChange,
  items,
  isLoading,
  error,
  isApplying,
  onApply,
}: ReferencePermitDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>เลือก Permit ที่เคยสร้างเพื่อใช้เป็นแม่แบบ</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            placeholder="ค้นหา Permit No, ประเภท หรือรายละเอียดงาน..."
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          <div className="max-h-[420px] space-y-3 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                กำลังโหลด...
              </div>
            ) : items.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                ไม่พบ Permit ที่คุณเคยสร้าง
              </div>
            ) : (
              items.map((permit) => (
                <Card key={permit.permitId} className="shadow-sm">
                  <CardContent className="flex items-start justify-between gap-3 py-4">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
                        <span>{permit.permitNumber}</span>
                        <Badge variant="secondary">{permit.permitType}</Badge>
                      </div>
                      <p className="text-sm text-gray-800">{permit.workDescription || permit.title}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                        {permit.plannedStart ? (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(permit.plannedStart).toLocaleDateString("th-TH")}
                          </span>
                        ) : null}
                        <span className="flex items-center gap-1">
                          <UserIcon className="h-4 w-4" />
                          {permit.requesterName}
                        </span>
                        {permit.locationName ? (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {permit.locationName}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="destructive"
                      className="min-w-[90px] bg-orange-500 text-white hover:bg-orange-600"
                      disabled={isApplying}
                      onClick={() => onApply(permit.permitId)}
                    >
                      {isApplying ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          กำลังคัดลอก...
                        </>
                      ) : (
                        "เลือก"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
        <DialogFooter className="justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ปิด
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
